import React, { useMemo, useState } from 'react';
import { Column } from 'Shared/components';
import { useAppContext } from 'QuoteApp/services/providers';
import { NoResults } from '../../NoResults';
import { ReportDetailHeaderRow } from '../../ReportDetailHeaderRow';
import { DriverReportType } from '../../types';
import styles from './styles.module.css';
import PriorPolicyForm from './PriorPolicyForm';
import { Button } from '@clearcover/atlas';
import { useEdit } from 'QuoteApp/utils/contexts/Edit';
import AddPriorPolicy from './PriorPolicyForm/AddPriorPolicy';
import { useAuth0 } from 'Auth';
import { editDriverReportPermissionsPolicy } from 'Admin/authPolicies';

const columnVariant = 'eighth';

const headers = [
  'CARRIER',
  'POLICY #',
  'EFFECTIVE',
  'END',
  'AM BEST',
  'STATE',
  'BI LIMIT',
  'CSL LIMIT',
  'PIP LIMIT',
];

export const PriorPolicies: DriverReportType = ({ driverId }) => {
  const [isAdding, setIsAdding] = useState(false);
  const { details } = useAppContext();
  const priorPolicies = details.priorPolicies?.[driverId];
  const { editingId, changeEditingId } = useEdit();

  const { user } = useAuth0();

  const canEditReports = editDriverReportPermissionsPolicy(
    user?.effectiveGrants || []
  );

  const canChallenge = useMemo(
    () =>
      canEditReports &&
      priorPolicies?.some(policy =>
        policy.commands?.some(
          (command: { name: string }) =>
            command.name === 'challenge_prior_policy'
        )
      ),
    [canEditReports, priorPolicies]
  );

  const canAdd = useMemo(() => {
    const driverCommands = details.commands.drivers[driverId]?.commands || [];
    return (
      canEditReports &&
      driverCommands.some(
        (command: { name: string }) => command.name === 'add_prior_policy'
      )
    );
  }, [canEditReports, details.commands.drivers, driverId]);

  const onCancelAdd = () => {
    setIsAdding(false);
    changeEditingId(null);
  };

  return (
    <div
      className={styles.priorPoliciesContainer}
      data-testid="prior-policies-section"
    >
      {canAdd && (
        <div className={styles.addRecordButton}>
          <Button
            variant="secondary"
            size="sm"
            className={styles.addRecordButton}
            disabled={editingId !== null}
            onClick={() => {
              setIsAdding(true);
              changeEditingId(`temp-prior-policy`);
            }}
          >
            + Add Record
          </Button>
        </div>
      )}
      {/* Header */}
      <ReportDetailHeaderRow
        removeCheckSpacer
        canChallenge={canChallenge}
        shortSource
        rowClassName={styles.priorPolicyHeaderRow}
      >
        {headers.map((header, i) => (
          <Column
            key={i}
            variant={columnVariant}
            className={styles.headerColumn}
          >
            {header}
          </Column>
        ))}
      </ReportDetailHeaderRow>

      {/* Body */}
      <>
        {priorPolicies?.map((policy, index) => {
          return (
            <PriorPolicyForm
              priorPolicy={policy.attributes}
              key={policy.attributes.policyNumber || index}
              canChallenge={!!canChallenge}
              commands={policy.commands}
              driverId={driverId}
              onCancelAdd={onCancelAdd}
            />
          );
        })}
      </>
      {priorPolicies?.length === 0 && !isAdding && (
        <NoResults isVisible>No prior policies</NoResults>
      )}
      {isAdding && (
        <AddPriorPolicy
          onCancelAdd={onCancelAdd}
          driverId={driverId}
          onSuccess={() => {
            setIsAdding(false);
          }}
        />
      )}
    </div>
  );
};
